import { useMemo } from 'react';

import { FlattenedOrderStatuses } from '@sg/graphql-schema';

import { getTimeToInterpolate } from '../useHomeBannerInfo';
import { useHomeLastOrderStatus } from '../useHomeLastOrderStatus';
import { useHomeNavigation } from '../useHomeNavigation';

// ─────────────────────────────────────────────────────────────────────────────

export const useHomePendingOrderHeader = (): PendingOrderHeader | undefined => {
  const lastOrder = useHomeLastOrderStatus('cache-and-network');

  const { navigateToOrderStatus } = useHomeNavigation(lastOrder.orderId);

  // ─── Derived ───────────────────────────────────────────────────────────────

  return useMemo(
    () => getPendingOrderHeader({ lastOrder, onPress: navigateToOrderStatus }),
    [lastOrder, navigateToOrderStatus],
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPendingOrderHeader(params: GetPendingOrderHeaderParams) {
  const { lastOrder, onPress } = params;

  const { orderId, orderStatus, orderTime } = lastOrder;

  if (!orderId || !orderStatus || !orderTime) return;
  if (!PENDING_ORDER_STATUSES.has(orderStatus)) return;

  const orderType = lastOrder.orderType;
  const startTime = lastOrder.startTime ?? '';
  const endTime = lastOrder.endTime ?? '';

  return {
    eta: getTimeToInterpolate({ orderStatus, orderTime, orderType, startTime, endTime }),
    orderStatus,
    onPress,
  };
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PENDING_ORDER_STATUSES = new Set<FlattenedOrderStatuses>([
  FlattenedOrderStatuses.Received,
  FlattenedOrderStatuses.Preparing,
  FlattenedOrderStatuses.ReadyForCourier,
  FlattenedOrderStatuses.Delivering,
]);

// ─── Types ───────────────────────────────────────────────────────────────────

type GetPendingOrderHeaderParams = {
  lastOrder: ReturnType<typeof useHomeLastOrderStatus>;
  onPress: () => void;
};

type PendingOrderHeader = {
  eta: string;
  orderStatus: FlattenedOrderStatuses;
  onPress: () => void;
};
